import React from "react";
import { motion } from "framer-motion";
import { Shield, Target, Award, Users, History, TrendingUp } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { StatsCard } from "@/components/Cards";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function CompanyEn() {
  const companyStatsEn = [
    { id: "founded", icon: <History className="w-8 h-8" />, value: "2020", label: "Founded" },
    { id: "experience", icon: <TrendingUp className="w-8 h-8" />, value: "5+", label: "Years Experience" },
    { id: "employees", icon: <Users className="w-8 h-8" />, value: "150+", label: "Expert Employees" },
    { id: "capacity", icon: <Award className="w-8 h-8" />, value: "27,000", label: "Tons Annual Capacity" }
  ];

  const valuesEn = [
    {
      icon: <Shield className="w-12 h-12 text-primary" />,
      title: "Quality",
      description: "We maintain the highest quality standards in every stage of production with our ISO certified processes and continuous improvement approach."
    },
    {
      icon: <Target className="w-12 h-12 text-primary" />,
      title: "Innovation",
      description: "We follow the latest technologies in the aluminum industry and continuously invest in R&D to offer innovative solutions to our customers."
    },
    {
      icon: <Award className="w-12 h-12 text-primary" />,
      title: "Excellence",
      description: "We aim for excellence in every project with our expert team and years of experience, exceeding customer expectations."
    },
    {
      icon: <Users className="w-12 h-12 text-primary" />,
      title: "Reliability",
      description: "We build long-term partnerships with our customers based on trust, transparency, and consistent service quality."
    }
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={IMAGES.HERO_INDUSTRIAL_2} 
            alt="Company" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl font-bold mb-6"
          >
            About MIMALUM
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl max-w-3xl mx-auto leading-relaxed"
          >
            Leading aluminum manufacturer since 2020, serving construction and industrial sectors
            with integrated production facilities and quality-focused approach.
          </motion.p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold mb-6 text-primary">Our Story</h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Founded in 2020, MIMALUM set out with a bold vision: to become a modern, technology-driven force in Turkey's aluminum extrusion industry. What began as an ambitious startup has quickly grown into a dynamic, integrated manufacturer serving clients across Turkey and beyond.
                </p>
                <p>
                  In just a few years, we have built state-of-the-art facilities spanning 26,000 m², housing 4 extrusion lines, advanced coating facilities, and a fully integrated production chain — from foundry to finished product.
                </p>
                <p>
                  With a team of 150+ professionals and an unwavering commitment to quality, innovation, and customer satisfaction, MIMALUM is shaping the future of aluminum manufacturing.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl"
            >
              <img 
                src={IMAGES.EKRAN_G_R_NT_S_2026_02_07_202330_81} 
                alt="Production Facility" 
                className="w-full aspect-video lg:aspect-square object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl font-bold mb-6">
              Our Achievements
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Numbers that reflect our commitment to excellence and continuous growth in the aluminum industry.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {companyStatsEn.map((stat, index) => (
              <motion.div key={index} variants={staggerItem}>
                <StatsCard stat={stat} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl font-bold mb-6">
              Our Values
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-muted-foreground max-w-3xl mx-auto">
              The core principles that guide our operations and define our commitment to customers and stakeholders.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {valuesEn.map((value, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-card p-8 rounded-lg border hover:shadow-lg transition-shadow duration-300"
              >
                <div className="mb-6">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-semibold mb-4">{value.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <p className="text-lg leading-relaxed text-primary-foreground/90">
                To be the preferred partner in the aluminum industry by providing high-quality, 
                innovative, and sustainable solutions that exceed customer expectations. We are committed 
                to continuous improvement, environmental responsibility, and contributing to the success 
                of our customers and community.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center lg:text-left"
            >
              <h2 className="text-3xl font-bold mb-6">Our Vision</h2>
              <p className="text-lg leading-relaxed text-primary-foreground/90">
                To be recognized as the leading integrated aluminum manufacturer in the region, 
                setting industry standards for quality, innovation, and sustainability. We envision 
                a future where our products and services contribute to building a more sustainable 
                and efficient world.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}